// Feed-cost normalization — the owning module for the $/lb figures that the
// Admin "Feed Costs" panel edits and that every feed-cost consumer reads.
//
// WHY THIS EXISTS
// ---------------
// The panel's fields are `<input type="number">`, whose `e.target.value` is a
// STRING. Those strings used to flow straight through `saveFeedCosts()` into
// the FeedCosts context, into `app_store['ppp-feed-costs-v1']`, AND into
// `perLbStarterCost` / `perLbStandardCost` / `perLbGritCost` / `perLbFeedCost`
// on every active broiler batch, pig feeder group, layer group and layer batch
// — each of which was then persisted. Downstream, `FeedCostsPanel`'s
// `.toFixed(3)` and `PigFeedView`'s `feedCosts.pig.toFixed(3)` threw a
// deterministic TypeError, because strings have no `toFixed`.
//
// The fix belongs at the SAVE and LOAD boundaries, never at the render sites:
//   * Fixing only the render sites would leave the strings persisted, and would
//     leave the six `feedCosts.<key> || 0` fan-out writes copying strings into
//     batch records.
//   * The LOAD side matters as much as the save side, though it is worth being
//     precise about what it does and does not fix. Normalizing on read makes
//     the IN-MEMORY costs numeric, which is what stops the crash on every
//     surface reading them even when `app_store` still holds strings from an
//     earlier build; and it keeps the cold-boot `ppp-v4` auto-activate path
//     from stamping fresh strings onto batch records. It does NOT rewrite batch
//     rows that already carry a string cost — that write is gated on the field
//     being empty. Those are repaired by the next successful save, which
//     re-fans normalized values to the active records.
//
// Kept free of React and Supabase so it can be imported from anywhere.

// The documented contract of the feed-costs object (see FeedCostsContext).
//
// Deliberately NOT `as const`. A readonly tuple would be the tidier type, but
// the five keys are not the contract this module enforces — the contract is
// that UNKNOWN KEYS SURVIVE, which is why normalizeFeedCosts spreads `src`
// first. Typing the return as an exact five-key object would state the opposite
// of what the code does and invite a future caller to drop the extras.
export const FEED_COST_KEYS = ['starter', 'grower', 'layer', 'pig', 'grit'];

// An open record: the five keys are guaranteed numeric after normalization,
// and anything else a different build wrote is carried through untouched.
export type FeedCosts = Record<string, unknown>;

/**
 * Coerce one raw feed-cost cell to a finite, non-negative number.
 *
 * Coercion idiom is `Number()`, deliberately — it is what the surrounding
 * arithmetic already did implicitly (`local[key] * 100`). `parseFloat()` would
 * NOT be equivalent: `parseFloat(null)` is `NaN` where `null * 100` is `0`, and
 * `parseFloat('1.5abc')` is `1.5` where `Number('1.5abc')` is `NaN` — we want
 * the strict reading, so trailing junk is rejected rather than silently kept.
 *
 * Blank means ZERO, not a validation error. That is forced by the existing
 * render: the input is `value={local[key] || ''}`, so a stored 0 already
 * displays as an empty field. Were blank an error, a freshly loaded panel
 * (all zeros, therefore all blank) could never be saved.
 *
 * Negatives clamp to 0. The input already carries `min="0"`; this is the
 * backstop for the paths that bypass it, and a negative $/lb has no meaning.
 */
export function normalizeFeedCostValue(raw: unknown): number {
  // Only numbers and strings are meaningful here. Anything else (booleans,
  // arrays, objects, undefined) would coerce to a misleading value —
  // `Number(true)` is 1, `Number([7])` is 7 — so reject the type outright.
  if (typeof raw !== 'number' && typeof raw !== 'string') return 0;
  const n = Number(raw);
  if (!Number.isFinite(n)) return 0; // NaN, Infinity, 'abc', '1.5abc'
  return n < 0 ? 0 : n;
}

/**
 * Normalize a whole feed-costs object.
 *
 * Guarantees all five keys are present as finite, non-negative numbers.
 * Unknown keys are preserved untouched: this replaces the
 * `{starter: 0, grower: 0, layer: 0, pig: 0, grit: 0, ...stored}` spread that
 * previously stood at each boundary, which also preserved extras — a save must
 * not silently drop a key some other build wrote.
 */
export function normalizeFeedCosts(raw: unknown): FeedCosts {
  const src: Record<string, unknown> = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const out = {...src};
  for (const key of FEED_COST_KEYS) out[key] = normalizeFeedCostValue(src[key]);
  return out;
}
